import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App.tsx";
import "./index.css";

declare const __APP_VERSION__: string;

// Clear stale auth tokens before Supabase client initializes on recovery pages
// This prevents "User from sub claim in JWT does not exist" errors
const hash = window.location.hash;
const isRecoveryPage = ["/reset-password", "/aluno/reset-password"].includes(window.location.pathname);
if (isRecoveryPage && hash.includes("type=recovery")) {
  // Remove stale Supabase auth keys so the recovery token is processed cleanly
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
      localStorage.removeItem(key);
    }
  });
}

// ── Chat custom domain detection ──
const CHAT_HOSTNAMES = ["chatpersonaifitness.com", "www.chatpersonaifitness.com"];
const isChatCustomDomain = CHAT_HOSTNAMES.includes(window.location.hostname);

// If user lands on the chat custom domain but NOT on a /chat route, redirect to /chat/login
if (isChatCustomDomain && !window.location.pathname.startsWith("/chat")) {
  window.location.replace("/chat/login");
}

// Dynamic manifest: professor vs student (and early tenant manifest for student routes)
const isStudentRoute = window.location.pathname.startsWith("/aluno");
const replaceManifestLink = (href: string) => {
  document.querySelectorAll('link[rel="manifest"]').forEach((node) => {
    const existingHref = node.getAttribute("href");
    if (existingHref?.startsWith("blob:")) URL.revokeObjectURL(existingHref);
    node.remove();
  });

  const link = document.createElement("link");
  link.rel = "manifest";
  link.href = href;
  document.head.appendChild(link);
};

// ── Chat PWA rescue: if the installed Chat PWA opens outside /chat/*, redirect immediately ──
const _isStandalonePwa = (() => {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
  } catch { return false; }
})();
const _isChatPwaContext = (() => {
  try {
    return (
      localStorage.getItem("personai_chat_install_context") === "1" ||
      sessionStorage.getItem("personai_chat_install_context") === "1"
    );
  } catch { return false; }
})();
const _bootstrapPath = window.location.pathname;
const _isOnChatRoute = _bootstrapPath.startsWith("/chat");

if (_isStandalonePwa && _isChatPwaContext && !_isOnChatRoute) {
  // The Chat PWA opened on a non-chat route (e.g. /aluno/login, /)
  // Hard redirect before React mounts
  window.location.replace("/chat/login?source=pwa-rescue");
}

{
const searchParams = new URLSearchParams(window.location.search);
const tokenFromQuery = searchParams.get("token");
if (tokenFromQuery) {
  localStorage.setItem("student_token", tokenFromQuery);
}
const studentToken = tokenFromQuery || localStorage.getItem("student_token");
const isChatRoute = window.location.pathname.startsWith("/chat") || isChatCustomDomain;

if (isChatRoute) {
  try {
    localStorage.setItem("personai_chat_pwa_hint", "1");
    sessionStorage.setItem("personai_chat_pwa_hint", "1");
    localStorage.setItem("personai_chat_install_context", "1");
    sessionStorage.setItem("personai_chat_install_context", "1");
  } catch {
    /* noop */
  }
}

if (isStudentRoute && studentToken && import.meta.env.VITE_SUPABASE_PROJECT_ID) {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const appOrigin = encodeURIComponent(window.location.origin);
  replaceManifestLink(
    `https://${projectId}.supabase.co/functions/v1/pwa-manifest?token=${encodeURIComponent(studentToken)}&origin=${appOrigin}&v=${encodeURIComponent(__APP_VERSION__)}&launch=${Date.now()}`,
  );

  // Early apple-touch-icon injection from cached tenant data (iOS reads this for A2HS icon)
  try {
    const cachedLogo = localStorage.getItem("tenant_logo_url");
    const cachedName = localStorage.getItem("tenant_name");
    const cachedColor = localStorage.getItem("tenant_brand_color");
    if (cachedLogo) {
      import("@/lib/dynamicManifest").then(({ applyTenantManifest }) => {
        applyTenantManifest(cachedName || undefined, cachedLogo, cachedColor || undefined);
      });
    }
  } catch { /* noop */ }
} else {
  // Only serve professor manifest on actual professor routes (dashboard, login, etc.)
  // Public routes like /anamnese, /cadastro should NOT trigger install prompts
  const publicRoutes = ["/anamnese", "/cadastro", "/aprovar-aluno", "/accept-invite", "/video-demo", "/base-conhecimento-nutricao"];
  const isPublicRoute = publicRoutes.some(r => window.location.pathname.startsWith(r));
  
  if (isChatRoute) {
    // PersonAI Chat has its own dedicated PWA manifest
    replaceManifestLink(`/manifest-kyra.json?v=${encodeURIComponent(__APP_VERSION__)}`);
    // Set apple-touch-icon for iOS
    const existingIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (existingIcon) existingIcon.remove();
    const iconLink = document.createElement("link");
    iconLink.rel = "apple-touch-icon";
    iconLink.href = "/pwa-chat-192x192.png";
    document.head.appendChild(iconLink);
    // Update apple-mobile-web-app-title
    let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement;
    if (titleMeta) titleMeta.content = "PersonAI Chat";
    // Update theme-color
    let themeMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (themeMeta) themeMeta.content = "#0a0a0a";
    // Update document title
    document.title = "PersonAI Chat - Assistente Inteligente";
  } else if (isPublicRoute) {
    // Remove any manifest to prevent install prompts on public pages
    document.querySelectorAll('link[rel="manifest"]').forEach((node) => node.remove());
  } else {
    const fallbackManifest = isStudentRoute ? "/manifest.json" : "/manifest-professor.json";
    replaceManifestLink(`${fallbackManifest}?v=${encodeURIComponent(__APP_VERSION__)}`);
  }
}

// Auto-reload when a new service worker takes control (seamless PWA updates)
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Only reload if visible — avoid disrupting background tabs
    if (document.visibilityState === "visible") {
      window.location.reload();
    }
  });
}

// PWA update check — auto-apply updates seamlessly
const UPDATE_CHECK_INTERVAL_MS = 60_000;

const updateSW = registerSW({
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;

    const checkForUpdates = () => {
      registration.update().catch(() => undefined);
    };

    checkForUpdates();
    window.setInterval(checkForUpdates, UPDATE_CHECK_INTERVAL_MS);
    window.addEventListener("focus", checkForUpdates);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkForUpdates();
    });
  },
  onNeedRefresh() {
    console.log("[PWA] New version available — applying update");
    // Auto-apply: controllerchange listener (line above) handles the reload
    updateSW(true);
  },
  onOfflineReady() {
    console.log("[PWA] Offline ready");
  },
});

createRoot(document.getElementById("root")!).render(<App />);
}
